import React, { useState, useEffect } from "react";
import {
  Button,
  DatePicker,
  Modal,
  Tabs,
  Typography,
  Divider,
  App,
} from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useCalendar } from "../../../hooks/useCalendar";
import {
  CalendarEntity,
  EducationLevel,
  UpdateMultipleWeeksRequest,
} from "../../../services/calendar/calendar.types";
import ActivityIndicator from "../../../components/ActivityIndicator";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import styles from "./styles.module.css";
import {
  parseUTCDate,
  toUTCDate,
  toUTCISOString,
  getOrderErrorWeeks,
  formatDate,
  getOverlappingWeeksAll,
  detectOverlapsAll,
  hasCalendarErrorsAll,
} from "../../../utils/calendarUtils";

dayjs.extend(utc);

const { Title, Text } = Typography;

type LevelTab = {
  // URL slug (`?nivel=`), in Spanish because it is user-facing.
  slug: string;
  level: EducationLevel;
  label: string;
  // Weeks the backend creates when initializing this level's cycle.
  initialWeeks: number;
};

// Preescolar first: it is the default tab.
const LEVEL_TABS: LevelTab[] = [
  { slug: "preescolar", level: "KINDER", label: "Preescolar", initialWeeks: 36 },
  { slug: "primaria", level: "PRIMARIA", label: "Primaria", initialWeeks: 38 },
];

const resolveTab = (slug: string | null): LevelTab =>
  LEVEL_TABS.find((tab) => tab.slug === slug) ?? LEVEL_TABS[0];

const Calendar: React.FC = () => {
  const { notification } = App.useApp();
  const { calendars, loading, fetchCalendar, initCalendar, updateWeekDate } =
    useCalendar();

  // The URL is the source of truth for the active level, so a view is
  // deep-linkable and survives refreshes (same pattern as the Papelera page).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get("nivel"));
  const activeLevel = activeTab.level;

  const [isInitModalVisible, setIsInitModalVisible] = useState(false);
  const [initStartDate, setInitStartDate] = useState<dayjs.Dayjs | null>(null);
  const [editingWeek, setEditingWeek] = useState<number | null>(null);
  const [editingDate, setEditingDate] = useState<dayjs.Dayjs | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Pending edits are buffered PER LEVEL: switching tabs must neither drop the
  // buffer nor let one level's dates reach the other calendar.
  const [previewByLevel, setPreviewByLevel] = useState<
    Partial<Record<EducationLevel, CalendarEntity[]>>
  >({});

  // Nuevos estados para el modal de consulta de ajuste automático
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [pendingDateChange, setPendingDateChange] = useState<{
    week: number;
    date: dayjs.Dayjs;
  } | null>(null);

  const calendar = calendars[activeLevel] ?? [];
  const previewCalendar = previewByLevel[activeLevel] ?? [];
  const showPreview = previewCalendar.length > 0;
  const displayedCalendar = showPreview ? previewCalendar : calendar;
  // Last week of the cycle, read from the data instead of a hardcoded total, so
  // the copy stays true whatever the level's length is.
  const lastWeek = displayedCalendar.reduce(
    (max, week) => Math.max(max, week.week),
    0
  );

  const setPreviewForActiveLevel = (weeks: CalendarEntity[] | null) => {
    setPreviewByLevel((previous) => {
      const next = { ...previous };
      if (weeks === null) {
        delete next[activeLevel];
      } else {
        next[activeLevel] = weeks;
      }
      return next;
    });
  };

  useEffect(() => {
    // Only the level being shown is fetched, and only while it is unfetched
    // (`null`); an already loaded level is served straight from the store.
    if (calendars[activeLevel] === null) {
      fetchCalendar(activeLevel);
    }
  }, [activeLevel, calendars, fetchCalendar]);

  const handleTabChange = (slug: string) => {
    // Leaving a tab only closes the transient editors; the level's preview
    // buffer survives so pending edits are still there when the user returns.
    setEditingWeek(null);
    setEditingDate(null);
    setShowAdjustModal(false);
    setPendingDateChange(null);
    setShowConfirmModal(false);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("nivel", slug);
      return next;
    });
  };

  const handleInitialize = async () => {
    if (!initStartDate) return;

    try {
      await initCalendar(activeLevel, {
        start_date: toUTCISOString(initStartDate),
      });
      setIsInitModalVisible(false);
      setInitStartDate(null);
    } catch (error) {
      notification.error({
        message: "Error",
        description:
          "Error al inicializar el calendario. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
      console.error("Error initializing calendar:", error);
    }
  };

  const handleDateClick = (week: CalendarEntity) => {
    setEditingWeek(week.week);
    setEditingDate(parseUTCDate(week.start_date));
  };

  const handleDateSave = () => {
    // Solo cerrar la edición, el guardado final se hace con el botón GUARDAR
    setEditingWeek(null);
    setEditingDate(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  // Función para manejar el click del botón GUARDAR principal
  const handleSaveAll = () => {
    if (showPreview) {
      // Si hay vista previa, mostrar modal de confirmación final
      setShowConfirmModal(true);
    } else {
      notification.info({
        message: "Sin cambios",
        description: "No hay cambios pendientes por guardar.",
        placement: "top",
        duration: 3,
      });
    }
  };

  // Función para ejecutar el guardado final (solo del nivel activo)
  const handleFinalConfirm = async () => {
    if (!showPreview) return;

    // VALIDAR SOLAPAMIENTOS antes de guardar
    const hasOverlaps = detectOverlapsAll(previewCalendar);
    if (hasOverlaps) {
      const overlappingWeeks = getOverlappingWeeksAll(previewCalendar);
      notification.error({
        message: "Error: Fechas solapadas",
        description: `Las semanas ${overlappingWeeks.join(
          ", "
        )} tienen fechas duplicadas. Por favor corrige estas fechas antes de guardar.`,
        placement: "top",
        duration: 8,
      });
      return; // No continuar con el guardado
    }

    // Encontrar qué semanas cambiaron
    const changedWeeks = previewCalendar.filter((previewWeek) => {
      const originalWeek = calendar.find((cw) => cw.week === previewWeek.week);
      return originalWeek && originalWeek.start_date !== previewWeek.start_date;
    });

    if (changedWeeks.length === 0) return;

    try {
      // Enviar todas las semanas juntas en una sola llamada
      if (changedWeeks.length === 1) {
        // Formato individual para una sola semana
        await updateWeekDate(activeLevel, {
          week: changedWeeks[0].week,
          start_date: changedWeeks[0].start_date,
        });
      } else {
        // Formato masivo para múltiples semanas
        const multipleWeeksData: UpdateMultipleWeeksRequest = {
          weeks: changedWeeks.map((week) => ({
            week: week.week,
            start_date: week.start_date,
          })),
        };
        await updateWeekDate(activeLevel, multipleWeeksData);
      }

      // Limpiar estados después del guardado exitoso
      setPreviewForActiveLevel(null);
      setShowConfirmModal(false);

      notification.success({
        message: "Cambios guardados",
        description: `Las fechas del calendario de ${activeTab.label} han sido actualizadas correctamente.`,
        placement: "top",
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: "Error al guardar",
        description:
          "Ocurrió un error al intentar guardar los cambios. Por favor, inténtalo de nuevo.",
        placement: "top",
        duration: 5,
      });
    }
  };

  const handleDateCancel = () => {
    setEditingWeek(null);
    setEditingDate(null);
    setPreviewForActiveLevel(null);
  };

  // Función para aplicar cambio solo a la semana seleccionada (sin ajustar posteriores)
  const applyDateChangeOnly = (targetWeek: number, newDate: dayjs.Dayjs) => {
    // Usar previewCalendar si existe, o calendar como base
    const baseCalendar = showPreview ? previewCalendar : calendar;

    const updatedCalendar = baseCalendar.map((week) =>
      week.week === targetWeek
        ? { ...week, start_date: toUTCISOString(newDate) }
        : week
    );
    setPreviewForActiveLevel(updatedCalendar);
  };

  // Función para aplicar cambio con ajuste automático (+7 días a posteriores)
  const applyDateChangeWithAdjustment = (
    targetWeek: number,
    newDate: dayjs.Dayjs
  ) => {
    // Usar previewCalendar si existe, o calendar como base
    const baseCalendar = showPreview ? previewCalendar : calendar;

    const updatedCalendar = [...baseCalendar];

    // Actualizar la semana objetivo
    const targetWeekIndex = updatedCalendar.findIndex(
      (w) => w.week === targetWeek
    );
    if (targetWeekIndex === -1) return;

    updatedCalendar[targetWeekIndex] = {
      ...updatedCalendar[targetWeekIndex],
      start_date: toUTCISOString(newDate),
    };

    // Actualizar todas las semanas posteriores sumando +7 días
    let currentDate = newDate;
    for (let i = targetWeekIndex + 1; i < updatedCalendar.length; i++) {
      const weekData = updatedCalendar[i];
      if (weekData.week > targetWeek) {
        currentDate = currentDate.add(7, "day");
        updatedCalendar[i] = {
          ...weekData,
          start_date: toUTCISOString(currentDate),
        };
      }
    }

    setPreviewForActiveLevel(updatedCalendar);
  };

  // Manejar respuesta del modal de consulta
  const handleAdjustModalResponse = (shouldAdjust: boolean) => {
    if (pendingDateChange) {
      if (shouldAdjust) {
        applyDateChangeWithAdjustment(
          pendingDateChange.week,
          pendingDateChange.date
        );
      } else {
        applyDateChangeOnly(pendingDateChange.week, pendingDateChange.date);
      }
    }
    setShowAdjustModal(false);
    setPendingDateChange(null);
  };

  // Función para detectar si la nueva fecha rompe la secuencia de +7 días
  const detectSequenceBreak = (
    targetWeek: number,
    newDate: dayjs.Dayjs
  ): boolean => {
    const subsequentWeeks = calendar
      .filter((w) => w.week > targetWeek)
      .sort((a, b) => a.week - b.week);
    if (subsequentWeeks.length === 0) return false;

    // Verificar si la nueva fecha + 7 días coincide con la próxima semana
    const nextWeek = subsequentWeeks[0];
    const expectedNextDate = newDate.add(7, "day");
    const actualNextDate = parseUTCDate(nextWeek.start_date);

    // Si la fecha esperada no coincide con la actual, hay ruptura de secuencia
    return !expectedNextDate.isSame(actualNextDate, "day");
  };

  // Función para manejar cambios en el DatePicker
  const handleDateChange = (date: dayjs.Dayjs | null) => {
    // Convertir la fecha seleccionada a UTC manteniendo la fecha exacta
    const utcDate = date ? toUTCDate(date) : null;
    setEditingDate(utcDate);

    if (utcDate && editingWeek) {
      const affectedWeeks = calendar.filter((w) => w.week > editingWeek);

      if (affectedWeeks.length > 0) {
        // Mostrar modal si la nueva fecha rompe la secuencia de +7 días
        const breaksSequence = detectSequenceBreak(editingWeek, utcDate);

        if (breaksSequence) {
          setPendingDateChange({ week: editingWeek, date: utcDate });
          setShowAdjustModal(true);
        } else {
          // No rompe secuencia, aplicar cambio solo a esta semana
          applyDateChangeOnly(editingWeek, utcDate);
        }
      } else {
        // No hay semanas posteriores, aplicar cambio directamente
        applyDateChangeOnly(editingWeek, utcDate);
      }
    } else {
      setPreviewForActiveLevel(null);
    }
  };

  // Función para validar si una fecha es seleccionable
  const isDateDisabled = (date: dayjs.Dayjs): boolean => {
    // Permitir seleccionar cualquier fecha - el feedback será en la previsualización
    return false;
  };

  // Tabla de semanas del nivel activo (cada pestaña muestra su propio calendario)
  const renderLevelPanel = () => {
    if (calendar.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Text>
            No hay calendario configurado para {activeTab.label}. Inicializa el
            calendario para comenzar.
          </Text>
        </div>
      );
    }

    return (
      <div className={styles.calendarContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.weekColumn}>
            <Text strong>Semana</Text>
          </div>
          <div className={styles.dateColumn}>
            <Text strong>Fecha inicio</Text>
          </div>
        </div>

        <Divider className={styles.divider} />

        <div className={styles.calendarList}>
          {displayedCalendar.map((week) => {
            const isChanged =
              showPreview &&
              previewCalendar.some(
                (pw) =>
                  pw.week === week.week &&
                  pw.start_date !==
                    calendar.find((cw) => cw.week === week.week)?.start_date
              );

            // Detectar errores específicos para esta semana
            const overlappingWeeks = getOverlappingWeeksAll(displayedCalendar);
            const orderErrorWeeks = getOrderErrorWeeks(displayedCalendar);

            const hasOverlap = overlappingWeeks.includes(week.week);
            const hasOrderError = orderErrorWeeks.includes(week.week);
            const hasAnyError = hasOverlap || hasOrderError;

            return (
              <div
                key={week.id}
                className={`${styles.calendarRow} ${
                  isChanged ? styles.changedRow : ""
                } ${hasAnyError ? styles.errorRow : ""}`}
              >
                <div className={styles.weekNumber}>
                  <Text>{week.week}</Text>
                  {hasAnyError && (
                    <div
                      className={styles.errorIndicator}
                      title={
                        hasOverlap && hasOrderError
                          ? "Fecha duplicada y orden incorrecto"
                          : hasOverlap
                          ? "Fecha duplicada con otra semana"
                          : "Orden de fecha incorrecto"
                      }
                    >
                      ❌
                    </div>
                  )}
                </div>
                <div className={styles.dateContainer}>
                  {editingWeek === week.week ? (
                    <div className={styles.editingContainer}>
                      <DatePicker
                        value={editingDate}
                        onChange={handleDateChange}
                        format="DD MMM YY"
                        placeholder="Seleccionar fecha"
                        className={styles.datePicker}
                        disabledDate={isDateDisabled}
                      />
                      <div className={styles.editingInfo}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {editingWeek < lastWeek
                            ? `Al cambiar esta fecha, las semanas ${
                                editingWeek + 1
                              }-${lastWeek} se pueden ajustar automáticamente (+7 días) si lo deseas`
                            : "Esta es la última semana del calendario"}
                        </Text>
                      </div>
                      <div className={styles.editButtons}>
                        <Button size="small" onClick={handleDateCancel}>
                          Cancelar
                        </Button>
                        <Button
                          size="small"
                          type="primary"
                          onClick={handleDateSave}
                          disabled={!editingDate}
                        >
                          {showPreview ? "Aplicar cambios" : "Guardar"}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={styles.dateDisplay}
                      onClick={() => handleDateClick(week)}
                    >
                      <Text className={styles.dateText}>
                        {formatDate(week.start_date)}
                      </Text>
                      {isChanged && (
                        <Text
                          className={styles.changedIndicator}
                          type="warning"
                        >
                          • Modificado
                        </Text>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Mostrar información de vista previa */}
        {showPreview && (
          <div className={styles.previewInfo}>
            <Text type="secondary">
              📋 Vista previa: Se muestran los cambios que se aplicarían. Las
              fechas marcadas como "Modificado" serán ajustadas automáticamente.
            </Text>

            {/* Mostrar errores específicos */}
            {(() => {
              const overlappingWeeks = getOverlappingWeeksAll(previewCalendar);
              const orderErrorWeeks = getOrderErrorWeeks(previewCalendar);
              const hasErrors =
                overlappingWeeks.length > 0 || orderErrorWeeks.length > 0;

              if (!hasErrors) return null;

              return (
                <div style={{ marginTop: 12 }}>
                  {overlappingWeeks.length > 0 && (
                    <div
                      style={{
                        marginBottom: 8,
                        padding: 12,
                        background: "var(--bo-error-bg)",
                        borderRadius: 6,
                        border: "1px solid var(--bo-error-border)",
                      }}
                    >
                      <Text type="danger" strong>
                        ❌ Fechas duplicadas en las semanas:{" "}
                        {overlappingWeeks.join(", ")}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Estas fechas están repetidas y deben corregirse.
                      </Text>
                    </div>
                  )}

                  {orderErrorWeeks.length > 0 && (
                    <div
                      style={{
                        marginBottom: 8,
                        padding: 12,
                        background: "var(--bo-warn-bg)",
                        borderRadius: 6,
                        border: "1px solid var(--bo-warn-border)",
                      }}
                    >
                      <Text type="warning" strong>
                        ❌ Orden incorrecto en las semanas:{" "}
                        {orderErrorWeeks.join(", ")}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Las fechas deben estar en orden cronológico ascendente.
                      </Text>
                    </div>
                  )}

                  <div
                    style={{
                      padding: 12,
                      background: "var(--bo-success-bg)",
                      borderRadius: 6,
                      border: "1px solid var(--bo-success-border)",
                    }}
                  >
                    <Text strong>
                      🚫 El botón GUARDAR está bloqueado hasta corregir estos
                      errores.
                    </Text>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // Solo la pestaña activa monta la tabla: así el estado de edición nunca se
  // comparte entre niveles.
  const tabItems = LEVEL_TABS.map((tab) => ({
    key: tab.slug,
    label: tab.label,
    children: tab.level === activeLevel ? renderLevelPanel() : null,
  }));

  if (loading) {
    return <ActivityIndicator />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitles}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Calendario semanal Emmi
          </Title>
          <Text type="secondary">
            Configura las fechas de inicio de cada semana Emmi para el ciclo
            escolar 2026–2027.
          </Text>
        </div>
        {calendar.length === 0 && (
          <Button
            type="primary"
            className={styles.saveButton}
            onClick={() => setIsInitModalVisible(true)}
          >
            Inicializar Calendario
          </Button>
        )}
        {calendar.length > 0 && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            className={styles.saveButton}
            onClick={handleSaveAll}
            disabled={showPreview && hasCalendarErrorsAll(previewCalendar)}
          >
            GUARDAR
          </Button>
        )}
      </div>

      <div className={styles.tabsSection}>
        <Tabs
          activeKey={activeTab.slug}
          onChange={handleTabChange}
          items={tabItems}
          destroyInactiveTabPane
        />
      </div>

      {/* Modal para inicializar calendario */}
      <Modal
        title={`Inicializar Calendario · ${activeTab.label}`}
        open={isInitModalVisible}
        onOk={handleInitialize}
        onCancel={() => {
          setIsInitModalVisible(false);
          setInitStartDate(null);
        }}
        okText="Inicializar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !initStartDate }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Selecciona la fecha de inicio del año escolar de {activeTab.label}:
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Se crearán las {activeTab.initialWeeks} semanas del ciclo, una por
            semana a partir de esa fecha.
          </Text>
        </div>
        <DatePicker
          value={initStartDate}
          onChange={setInitStartDate}
          format="DD MMM YYYY"
          placeholder="Seleccionar fecha de inicio"
          style={{ width: "100%" }}
        />
      </Modal>

      {/* Modal de consulta para ajuste automático */}
      <Modal
        title="¿Ajustar semanas posteriores?"
        open={showAdjustModal}
        onOk={() => handleAdjustModalResponse(true)}
        onCancel={() => handleAdjustModalResponse(false)}
        okText="Sí, ajustar automáticamente"
        cancelText="No, solo esta semana"
        okType="primary"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            {pendingDateChange &&
              `Has cambiado la fecha de la Semana ${
                pendingDateChange.week
              } a ${pendingDateChange.date.format("DD MMM YYYY")}.`}
          </Text>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text>
            ¿Quieres que las semanas posteriores se ajusten automáticamente
            sumando +7 días a partir de esta nueva fecha?
          </Text>
        </div>

        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: "var(--bo-success-bg)",
            borderRadius: 6,
            border: "1px solid var(--bo-success-border)",
          }}
        >
          <Text>
            <strong>Opción 1:</strong> Ajustar automáticamente - Las semanas{" "}
            {pendingDateChange?.week ? pendingDateChange.week + 1 : ""}-
            {lastWeek} se recalcularán sumando +7 días.
          </Text>
        </div>

        <div
          style={{
            marginTop: 8,
            padding: 12,
            background: "var(--bo-warn-bg)",
            borderRadius: 6,
            border: "1px solid var(--bo-warn-border)",
          }}
        >
          <Text>
            <strong>Opción 2:</strong> Solo esta semana - Únicamente se cambiará
            la fecha de la Semana {pendingDateChange?.week}.
          </Text>
        </div>
      </Modal>

      {/* Modal de confirmación final para el botón GUARDAR */}
      <Modal
        title="Confirmar guardado de cambios"
        open={showConfirmModal}
        onOk={handleFinalConfirm}
        onCancel={handleCancelConfirm}
        okText="Confirmar y guardar"
        cancelText="Cancelar"
        okType="primary"
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Estás a punto de guardar los siguientes cambios en el calendario de{" "}
            {activeTab.label}:
          </Text>
        </div>

        {showPreview && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>Cambios que se aplicarán:</Text>
            <div
              style={{
                maxHeight: 200,
                overflow: "auto",
                marginTop: 8,
                border: "1px solid var(--bo-card-border)",
                borderRadius: 4,
              }}
            >
              {previewCalendar
                .filter((week) => {
                  const originalWeek = calendar.find(
                    (cw) => cw.week === week.week
                  );
                  return (
                    originalWeek && originalWeek.start_date !== week.start_date
                  );
                })
                .map((week) => {
                  const originalWeek = calendar.find(
                    (cw) => cw.week === week.week
                  );
                  return (
                    <div
                      key={week.week}
                      style={{
                        padding: "6px 12px",
                        borderBottom: "1px solid var(--bo-card-border)",
                        backgroundColor: "var(--bo-warn-bg)",
                      }}
                    >
                      <Text>
                        Semana {week.week}:{" "}
                        {originalWeek
                          ? formatDate(originalWeek.start_date)
                          : ""}{" "}
                        → {formatDate(week.start_date)}
                      </Text>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: "var(--bo-warn-bg)",
            borderRadius: 6,
            border: "1px solid var(--bo-warn-border)",
          }}
        >
          <Text type="warning" strong>
            ⚠️ Una vez guardados, estos cambios se aplicarán como las fechas
            oficiales de desbloqueo.
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default Calendar;
